import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiBell, FiCalendar, FiUsers, FiCheck, FiClock, FiMapPin, FiEye, FiTrash2, FiCloudRain, FiUserPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { markNotificationAsRead, markNotificationAsUnread, deleteNotification } from '../services/notificationService';
import { formatDateWithWeekday } from '../utils/dateHelpers';
import { formatRoundDate, formatRoundTimeWithViewerNote } from '../utils/timeHelpers';
import { useNotifications } from '../contexts/NotificationContext';

function NotificationItem({ notification, onUpdate, showActions = true, compact = false }) {
  const navigate = useNavigate();
  const { updateUnreadCount } = useNotifications();
  const [loading, setLoading] = useState(false);

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'round_invitation':
      case 'round_response':
      case 'round_created':
      case 'round_cancelled':
      case 'round_reminder':
        return FiCalendar;
      case 'group_member_joined':
      case 'group_member_left':
      case 'group_update':
        return FiUsers;
      case 'friend_request_received':
      case 'friend_request_accepted':
        return FiUserPlus;
      case 'availability_request':
      case 'availability_provided':
        return FiClock;
      case 'weather_score_decreased':
        return FiCloudRain;
      default:
        return FiBell;
    }
  };

  // Get notification color based on priority and type
  const getNotificationColors = (type, priority) => {
    if (priority === 'high') {
      return {
        bg: 'bg-status-error/10',
        text: 'text-status-error',
        border: 'border-status-error/30'
      };
    }

    switch (type) {
      case 'round_invitation':
      case 'round_created':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          border: 'border-accent/30'
        };
      case 'round_response':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-600',
          border: 'border-blue-200'
        };
      case 'group_member_joined':
        return {
          bg: 'bg-purple-100',
          text: 'text-purple-600',
          border: 'border-purple-200'
        };
      case 'group_member_left':
        return {
          bg: 'bg-orange-100',
          text: 'text-orange-600',
          border: 'border-orange-200'
        };
      case 'weather_score_decreased':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-600',
          border: 'border-amber-200'
        };
      case 'friend_request_received':
      case 'friend_request_accepted':
        return {
          bg: 'bg-cyan-100',
          text: 'text-cyan-600',
          border: 'border-cyan-200'
        };
      default:
        return {
          bg: 'bg-surface',
          text: 'text-text-secondary',
          border: 'border-border-subtle'
        };
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Handle notification click
  const handleClick = async () => {
    // Mark as read if not already read
    if (!notification.read) {
      setLoading(true);
      await markNotificationAsRead(notification.id);
      if (onUpdate) {
        onUpdate({ ...notification, read: true });
      }
      setLoading(false);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'round_invitation':
      case 'round_response':
      case 'round_created':
      case 'round_cancelled':
      case 'round_reminder':
        if (notification.round_id) {
          navigate(`/rounds`);
        } else {
          navigate('/rounds');
        }
        break;
      case 'group_member_joined':
      case 'group_member_left':
      case 'group_update':
        if (notification.group_id) {
          navigate(`/group/${notification.group_id}`);
        } else {
          navigate('/groups');
        }
        break;
      case 'availability_request':
        // Navigate to availability page for availability request notifications
        if (notification.data?.redirectTo) {
          navigate(notification.data.redirectTo);
        } else {
          navigate('/my-availability');
        }
        break;
      case 'availability_provided':
        // Recipient set their availability — send the requester back into
        // the smart-schedule wizard with the right context pre-selected.
        if (notification.data?.redirectTo) {
          navigate(notification.data.redirectTo);
        } else if (notification.data?.groupId) {
          navigate(`/create-round/smart/when?group=${notification.data.groupId}`);
        } else {
          navigate('/create-round/smart/when?who=friends');
        }
        break;
      case 'weather_score_decreased':
        // Navigate to rounds page for weather notifications
        navigate('/rounds');
        break;
      case 'round_open_invite_created':
        // Open-invite broadcast - take the recipient straight to the round so
        // they can request a spot / join.
        if (notification.data?.shortCode) {
          navigate(`/round/${notification.data.shortCode}`);
        } else {
          navigate('/rounds');
        }
        break;
      case 'friend_request_received':
      case 'friend_request_accepted':
        navigate('/friends');
        break;
      default:
        // For other types, stay on notifications page
        break;
    }
  };

  // Handle toggle read/unread
  const handleToggleRead = async (e) => {
    e.stopPropagation();
    setLoading(true);
    
    const newReadState = !notification.read;
    const { error } = newReadState 
      ? await markNotificationAsRead(notification.id)
      : await markNotificationAsUnread(notification.id);
      
    if (!error) {
      // Update the notification bell count
      if (newReadState) {
        updateUnreadCount(-1); // Marked as read, decrease count
      } else {
        updateUnreadCount(1); // Marked as unread, increase count
      }
      
      // Update the notification in the list
      if (onUpdate) {
        onUpdate({ ...notification, read: newReadState });
      }
    }
    
    setLoading(false);
  };


  // Handle delete
  const handleDelete = async (e) => {
    e.stopPropagation();
    setLoading(true);
    
    const { error } = await deleteNotification(notification.id);
    if (!error && onUpdate) {
      onUpdate(null, notification.id); // Signal removal with ID
    }
    
    setLoading(false);
  };

  // Get round info from notification data
  const getRoundInfo = () => {
    const data = notification.data || {};
    if (data.date && data.time) {
      // Round shape for timeHelpers — accepts data with tz hints if present;
      // falls back gracefully (helper warns and uses UTC) when older
      // notifications were produced without these fields.
      const hasTzHints = data.timezone || (data.course_latitude != null && data.course_longitude != null);
      const { primary, secondary } = hasTzHints
        ? formatRoundTimeWithViewerNote(data, { hour12: true })
        : { primary: data.time, secondary: null };
      return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-text-secondary mt-2">
          <div className="flex items-center space-x-1">
            <SafeIcon icon={FiCalendar} className="w-4 h-4" />
            <span>
              {hasTzHints
                ? formatRoundDate(data, { weekday: 'short', day: 'numeric', month: 'short', year: undefined })
                : formatDateWithWeekday(data.date)}
            </span>
          </div>
          <div className="flex items-start space-x-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 mt-0.5" />
            <div className="flex flex-col">
              <span>{primary}</span>
              {secondary && (
                <span className="text-xs text-text-tertiary">{secondary}</span>
              )}
            </div>
          </div>
          {data.course && (
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiMapPin} className="w-4 h-4" />
              <span>{data.course}</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const colors = getNotificationColors(notification.type, notification.priority);
  const IconComponent = getNotificationIcon(notification.type);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, x: -50 }}
      whileHover={{ scale: compact ? 1 : 1.01 }}
      transition={{
        layout: { type: "spring", stiffness: 300, damping: 30 },
        exit: { duration: 0.2 }
      }}
      className={`
        relative p-4 rounded-lg border transition-all duration-200 cursor-pointer
        ${notification.read ? 'bg-surface border-border-subtle' : 'bg-subtle border-border-subtle'}
        ${compact ? 'p-3' : 'p-4'}
        hover:
      `}
      onClick={handleClick}
    >
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-card bg-opacity-75 rounded-lg flex items-center justify-center z-10">
          <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            {/* Icon */}
            <div className={`
              w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0
              ${colors.bg} ${colors.text} ${colors.border} border
            `}>
              <SafeIcon icon={IconComponent} className="w-4 h-4" />
            </div>
            <h4 className={`font-semibold text-text-primary ${compact ? 'text-sm' : 'text-base'}`}>
              {notification.title}
            </h4>
          </div>
          {/* Unread Indicator */}
          {!notification.read && (
            <div className="w-3 h-3 bg-accent rounded-full flex-shrink-0 mt-1"></div>
          )}
        </div>
        
        {/* Content flows full width */}
        <div className="space-y-2">
          <p className={`text-text-secondary ${compact ? 'text-xs' : 'text-sm'}`}>
            {notification.type === 'availability_request' ? (
              <>
                {notification.message.split('Click here').map((part, index) => (
                  <React.Fragment key={index}>
                    {part}
                    {index === 0 && (
                      <span className="text-accent underline font-medium">
                        Click here
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : (
              notification.message
            )}
          </p>
          
          {/* Round Info for round invitations */}
          {!compact && notification.type === 'round_invitation' && getRoundInfo()}
          {!compact && notification.type !== 'round_invitation' && getRoundInfo()}
          
          {/* Group Info */}
          {!compact && notification.data?.groupName && (
            <div className="flex items-center space-x-1 text-sm text-text-secondary">
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
              <span>{notification.data.groupName}</span>
            </div>
          )}
          
          {/* Timestamp and Actions */}
          <div className="flex items-center justify-between">
            <span className={`text-text-muted ${compact ? 'text-xs' : 'text-sm'}`}>
              {formatTime(notification.created_at)}
            </span>
            {/* Actions */}
            {showActions && !compact && (
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleToggleRead}
                  className={`p-1 transition-colors duration-200 ${
                    notification.read 
                      ? 'text-text-muted hover:text-text-secondary' 
                      : 'text-accent hover:text-accent'
                  }`}
                  title={notification.read ? "Mark as unread" : "Mark as read"}
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleDelete}
                  className="p-1 text-text-muted hover:text-status-error transition-colors duration-200"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-start space-x-3">
        {/* Icon */}
        <div className={`
          w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
          ${colors.bg} ${colors.text} ${colors.border} border
        `}>
          <SafeIcon icon={IconComponent} className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className={`font-semibold text-text-primary ${compact ? 'text-sm' : 'text-base'}`}>
                {notification.title}
              </h4>
              <p className={`text-text-secondary mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
                {notification.type === 'availability_request' ? (
                  <>
                    {notification.message.split('Click here').map((part, index) => (
                      <React.Fragment key={index}>
                        {part}
                        {index === 0 && (
                          <span className="text-accent underline font-medium">
                            Click here
                          </span>
                        )}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  notification.message
                )}
              </p>
              
              {/* Round Info for round invitations */}
              {!compact && notification.type === 'round_invitation' && getRoundInfo()}
              {!compact && notification.type !== 'round_invitation' && getRoundInfo()}
              
              {/* Group Info */}
              {!compact && notification.data?.groupName && (
                <div className="flex items-center space-x-1 text-sm text-text-secondary mt-2">
                  <SafeIcon icon={FiUsers} className="w-4 h-4" />
                  <span>{notification.data.groupName}</span>
                </div>
              )}
            </div>

            {/* Unread Indicator */}
            {!notification.read && (
              <div className="w-3 h-3 bg-accent rounded-full flex-shrink-0 mt-1 ml-2"></div>
            )}
          </div>

          {/* Timestamp and Actions */}
          <div className="flex items-center justify-between mt-3">
            <span className={`text-text-muted ${compact ? 'text-xs' : 'text-sm'}`}>
              {formatTime(notification.created_at)}
            </span>

            {/* Actions */}
            {showActions && !compact && (
              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleToggleRead}
                  className={`p-1 transition-colors duration-200 ${
                    notification.read 
                      ? 'text-text-muted hover:text-text-secondary' 
                      : 'text-accent hover:text-accent'
                  }`}
                  title={notification.read ? "Mark as unread" : "Mark as read"}
                >
                  <SafeIcon icon={FiEye} className="w-4 h-4" />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleDelete}
                  className="p-1 text-text-muted hover:text-status-error transition-colors duration-200"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default NotificationItem;